import React from 'react';
import colors from 'utils/colors';
import { NavLink } from 'react-router-dom';
import { ListHeader } from 'components/dashboard/components/list-header';
import shortid from 'shortid';
import { withRouter } from 'react-router';
import reduce from 'lodash/reduce';
import {
  Container,
  TableHeader,
  Table,
  ViewWrapper,
  TableRow,
  TableData,
} from 'components/dashboard/components/styled-components';
import { HoverTableRow } from 'components/styled-components';
import { iconPicker } from 'utils/functions';
import { Icon, NonIdealState, Intent } from '@blueprintjs/core';
import permissions from 'utils/permissions';
import hasPermission from 'utils/hasPermission';

const DEFAULT_NUM_FIELDS = '3'; //string value for number of columns in table

/* Creates row of th values for each field */
const TableHeaders = ({ headers }) => {
  const content = [];
  if (headers) {
    headers.forEach(header => {
      content.push(
        <TableHeader key={shortid.generate()}>{header}</TableHeader>
      );
    });
  }
  return <TableRow>{content}</TableRow>;
};

/* Uses the Row Content by sending it sliced data,
   and getting back the entry it needs for a row  */
const TableContent = ({ data, fields, userRole, history, appUser }) => {
  if (data['result'] && data.totalCount !== 0) {
    // loop through first 5 results
    return data['result'].slice(0, 5).map(item => (
      // link to the assessment details page, unless the user is a third party and
      // the assessment has not been completed by them yet. Then link to questionnaire
      <HoverTableRow
        key={`${item.vendorRequestID}-${shortid.generate()}`}
        onClick={() =>
          history.push(
            userRole === 'Third Party' &&
              item.requestStatus === 'Awaiting Third Party Completion'
              ? `/questionnaire/${item.vendorRequestID}`
              : `/assessments/${item.vendorRequestID}`
          )
        }
      >
        <RowContent dataRow={item} fields={fields} appUser={appUser} />
      </HoverTableRow>
    ));
  } else {
    return (
      <TableRow>
        <TableData colSpan={fields.length}>
          <NonIdealState
            icon="info-sign"
            description={
              <>There are no Third Party Assessments for this filter.</>
            }
          />
        </TableData>
      </TableRow>
    );
  }
};

/* Helper function for TableContents to create all of the entries for a row.
   Checks for different fields that need special formatting
*/
const RowContent = ({ dataRow, fields, appUser }) => {
  const result = reduce(
    fields,
    (result, field) => {
      if (field.toString().toLowerCase() === 'requeststatus') {
        // if the field is a status, add the icon next to it
        const icon = dataRow.statusCategory
          ? iconPicker(dataRow.statusCategory)
          : iconPicker('Pending');
        result.push(
          <>
            {/* check to see if user has permissions to view status. */}
            {hasPermission(appUser.permissions, [
              permissions.MODIFY_ALL_THIRD_PARTY_REQUESTS,
              permissions.MODIFY_OWNED_THIRD_PARTY_REQUESTS,
            ]) ? (
              // if user HAS permissions, show icon and status in table
              <div style={{ display: 'flex' }}>
                <Icon
                  icon={icon.icon}
                  intent={icon.intent}
                  style={{ marginRight: '0.25rem' }}
                />
                <div>{dataRow['requestStatus']}</div>
              </div>
            ) : // if user DOES NOT have permission, show 'submitted' in table, but ONLY if status is NOT 'Awaiting Third Party Completion'.
            // If status is 'Awaiting Third Party Completion' show that, and corresponding icon in table.
            dataRow.requestStatus === 'Awaiting Third Party Completion' ? (
              <div style={{ display: 'flex' }}>
                <Icon
                  icon={icon.icon}
                  intent={icon.intent}
                  style={{ marginRight: '0.25rem' }}
                />
                <div>{dataRow['requestStatus']}</div>
              </div>
            ) : (
              <div style={{ display: 'flex' }}>
                <Icon
                  icon="tick-circle"
                  intent={Intent.SUCCESS}
                  style={{ marginRight: '0.25rem' }}
                />
                <div>Submitted</div>
              </div>
            )}
          </>
        );
      } else if (dataRow[field]) {
        // if the attribute exists in the data, add it
        result.push(dataRow[field]);
      } else {
        // if there are nested attributes (i.e. vendorContact -> emailAddress) then
        // loop through and add the final reduced result to the table's result array
        const value = reduce(
          field,
          (result, val, index) => {
            if (index === 0) {
              result = dataRow[val];
            } else {
              result = result[val];
            }
            return result;
          },
          {}
        );
        result.push(value);
      }
      return result;
    },
    []
  );

  // set the color of the first entry to be blue, indicating a link
  result[0] = <span style={{ color: colors.primary }}>{result[0]}</span>;

  // adds each item in the result array to a TableData element with a key
  let content = [];
  result.forEach(item => {
    content.push(
      <TableData
        border={`2pt solid ${colors.brandingPrimary}`}
        key={shortid.generate()}
      >
        {item ? item : '-'}
      </TableData>
    );
  });
  return content;
};

export const ReviewTable = props => {
  return (
    <Container flexBasis={props.flexBasis}>
      <Table>
        <tbody>
          <TableRow>
            <th
              colSpan={props.fields ? props.fields.length : DEFAULT_NUM_FIELDS}
            >
              <ListHeader {...props} />
            </th>
          </TableRow>
          <TableHeaders headers={props.headers} />
          <TableContent {...props} />
          <TableRow>
            <th
              colSpan={props.fields ? props.fields.length : DEFAULT_NUM_FIELDS}
            >
              <NavLink
                to={{
                  pathname:
                    props.filter === 'upcoming' ? '/renewals' : '/assessments',
                  filterType: props.filterType,
                  filter: props.filter,
                  owned: props.owned,
                  filterAltName: props.filterAltName,
									displayFilter: props.displayFilter
                }}
              >
                <ViewWrapper>
                  View All ({props.data.totalCount || '0'})
                </ViewWrapper>
              </NavLink>
            </th>
          </TableRow>
        </tbody>
      </Table>
    </Container>
  );
};

export default withRouter(ReviewTable);
